import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getStock, getInventories, getCategories, getBrands, exportStockToExcel, updateMinimumQuantity } from '../lib/api';
import { downloadBlob } from '../lib/downloadHelper';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { ItemThumbnail } from '../components/ImageUploader';
import {
  Search, Download, Loader2, Package, AlertTriangle, Boxes, Printer, Check, X
} from 'lucide-react';

const Inventory = () => {
  const { isManager } = useAuth();
  const [searchParams] = useSearchParams();

  const [stock, setStock] = useState([]);
  const [inventories, setInventories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [selectedInventory, setSelectedInventory] = useState(searchParams.get('inventory_id') || 'all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(searchParams.get('low_stock') === 'true');

  // Inline min qty editing
  const [editingMinQty, setEditingMinQty] = useState(null); // stock id
  const [editMinQtyValue, setEditMinQtyValue] = useState('');
  const [savingMinQty, setSavingMinQty] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [stockRes, invRes] = await Promise.all([
        getStock({
          inventory_id: selectedInventory !== 'all' ? selectedInventory : undefined,
          low_stock_only: showLowStockOnly,
          search: searchQuery || undefined
        }),
        getInventories()
      ]);
      setStock(stockRes.data);
      setInventories(invRes.data);
    } catch (error) {
      toast.error('Failed to load inventory data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedInventory, showLowStockOnly]);
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportStockToExcel(selectedInventory !== 'all' ? selectedInventory : undefined);
      downloadBlob(response.data, `inventory_stock_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Inventory exported');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const getInventoryName = (id) => {
    const inv = inventories.find(i => i.id === id);
    return inv ? inv.inventory_name : 'Unknown';
  };

  const startEditMinQty = (item) => {
    setEditingMinQty(item.id);
    setEditMinQtyValue(String(item.minimum_quantity));
  };

  const cancelEditMinQty = () => {
    setEditingMinQty(null);
    setEditMinQtyValue('');
  };

  const saveMinQty = async (stockId) => {
    const val = parseInt(editMinQtyValue);
    if (isNaN(val) || val < 0) {
      toast.error('Enter a valid non-negative number');
      return;
    }
    setSavingMinQty(true);
    try {
      await updateMinimumQuantity(stockId, val);
      toast.success('Minimum quantity updated');
      setEditingMinQty(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingMinQty(false);
    }
  };

  const lowStockCount = stock.filter(s => s.is_low_stock).length;
  const totalStockValue = useMemo(() => stock.reduce((s, i) => s + i.total_value, 0), [stock]);

  return (
    <div className="space-y-6 animate-slide-in" data-testid="inventory-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="heading-2 text-[#1F2937]">Inventory Stock</h1>
          <p className="text-[#6B7280]">View and manage stock levels across all locations</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button onClick={handleExport} disabled={exporting} className="gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg" data-testid="export-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download size={18} />} Export Excel
          </Button>
          <Button onClick={() => window.print()} variant="outline" className="gap-2 rounded-lg border-[#0776BD] text-[#0776BD]" data-testid="print-btn">
            <Printer size={18} /> Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 print:hidden">
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#E6F2FA] rounded-xl"><Package size={22} className="text-[#0776BD]" /></div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total Items</p>
                <p className="text-2xl font-bold font-heading text-[#1F2937]">{stock.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-xl"><Boxes size={22} className="text-green-600" /></div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Total Value</p>
                <p className="text-2xl font-bold font-heading text-[#1F2937]">${totalStockValue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white rounded-xl border border-gray-200 shadow-soft">
          <CardContent className="p-5">
            <div className="flex items-center gap-4">
              <div className={`p-3 rounded-xl ${lowStockCount > 0 ? 'bg-[#F0C500]/20' : 'bg-gray-100'}`}>
                <AlertTriangle size={22} className={lowStockCount > 0 ? 'text-[#F0C500]' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-sm font-medium text-[#6B7280] uppercase tracking-wide">Low Stock</p>
                <p className={`text-2xl font-bold font-heading ${lowStockCount > 0 ? 'text-[#F0C500]' : 'text-[#1F2937]'}`}>{lowStockCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft print:hidden">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" size={20} />
              <Input placeholder="Search by name, code, or description..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 h-12 rounded-lg border-gray-300" data-testid="search-input" />
            </div>
            <Select value={selectedInventory} onValueChange={setSelectedInventory}>
              <SelectTrigger className="w-full md:w-[200px] h-12 rounded-lg border-gray-300" data-testid="inventory-filter">
                <Boxes size={18} className="mr-2 text-[#6B7280]" />
                <SelectValue placeholder="All Inventories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Inventories</SelectItem>
                {inventories.map(inv => <SelectItem key={inv.id} value={inv.id}>{inv.inventory_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button
              variant={showLowStockOnly ? "default" : "outline"}
              onClick={() => setShowLowStockOnly(!showLowStockOnly)}
              className={`gap-2 h-12 rounded-lg ${showLowStockOnly ? 'bg-[#F0C500] hover:bg-[#D9B100] text-[#1F2937]' : 'border-gray-300 text-[#6B7280] hover:bg-gray-50'}`}
              data-testid="low-stock-filter"
            >
              <AlertTriangle size={18} /> Low Stock Only
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stock Table */}
      <Card className="bg-white rounded-xl border border-gray-200 shadow-soft print:shadow-none print:border-0">
        <CardHeader className="print:hidden">
          <CardTitle className="font-heading text-lg text-[#1F2937] flex items-center gap-2">
            <Package size={20} className="text-[#0776BD]" /> Stock Items ({stock.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#0776BD]" /></div>
          ) : stock.length === 0 ? (
            <div className="py-12 text-center text-[#6B7280]">
              <Package size={48} className="mx-auto mb-4 opacity-50" />
              <p>No items found</p>
              <p className="text-sm">Try adjusting your filters or add some stock</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F6F7]">
                    <TableHead className="font-semibold text-[#1F2937]">Item</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Code</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Category</TableHead>
                    <TableHead className="font-semibold text-[#1F2937]">Location</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Qty</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Min Qty</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Unit Price</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-right">Total</TableHead>
                    <TableHead className="font-semibold text-[#1F2937] text-center">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stock.map((item) => (
                    <TableRow key={item.id} className={`hover:bg-[#F5F6F7] ${item.is_low_stock ? 'bg-amber-50/50' : ''}`} data-testid={`stock-row-${item.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <ItemThumbnail imageIds={item.image_ids} size={40} alt={item.item_name} />
                          <div>
                            <p className="font-medium text-[#1F2937]">{item.item_name}</p>
                            <p className="text-sm text-[#6B7280]">{item.brand_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm text-[#6B7280]">{item.internal_code}</TableCell>
                      <TableCell className="text-[#6B7280]">{item.category_name}</TableCell>
                      <TableCell className="text-[#6B7280]">{getInventoryName(item.inventory_id)}</TableCell>
                      <TableCell className="text-center">
                        <span className={`font-bold text-lg ${item.is_low_stock ? 'text-[#F0C500]' : 'text-[#1F2937]'}`}>{item.quantity}</span>
                        <span className="text-[#6B7280] ml-1 text-xs">{item.unit}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        {editingMinQty === item.id ? (
                          <div className="flex items-center gap-1 justify-center">
                            <Input
                              type="number" min="0" value={editMinQtyValue}
                              onChange={(e) => setEditMinQtyValue(e.target.value)}
                              onKeyDown={(e) => { if (e.key === 'Enter') saveMinQty(item.id); if (e.key === 'Escape') cancelEditMinQty(); }}
                              className="w-16 h-8 text-center text-sm rounded border-[#0776BD] p-1"
                              autoFocus
                              data-testid={`min-qty-input-${item.id}`}
                            />
                            <Button type="button" variant="ghost" size="icon" onClick={() => saveMinQty(item.id)} disabled={savingMinQty} className="h-7 w-7 hover:bg-green-50 text-green-600" data-testid={`min-qty-save-${item.id}`}>
                              {savingMinQty ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                            </Button>
                            <Button type="button" variant="ghost" size="icon" onClick={cancelEditMinQty} className="h-7 w-7 hover:bg-red-50 text-red-500" data-testid={`min-qty-cancel-${item.id}`}>
                              <X size={14} />
                            </Button>
                          </div>
                        ) : (
                          <span
                            className={`text-sm font-medium cursor-pointer px-2 py-1 rounded transition-colors ${isManager() ? 'hover:bg-[#E6F2FA] hover:text-[#0776BD]' : ''}`}
                            onClick={() => isManager() && startEditMinQty(item)}
                            title={isManager() ? 'Click to edit minimum quantity' : ''}
                            data-testid={`min-qty-${item.id}`}
                          >
                            {item.minimum_quantity}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-[#6B7280]">${item.unit_price.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-[#1F2937]">${item.total_value.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        {item.is_low_stock ? (
                          <Badge className="bg-[#F0C500]/20 text-[#1F2937] border-0 gap-1">
                            <AlertTriangle size={12} /> Low
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 border-0">In Stock</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Inventory;
